import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function LightingInstallationPage() {
  useEffect(() => {
    document.title = 'Lighting Installation Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional lighting installation services in Watford. Indoor and outdoor lighting by NICEIC electricians. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/lighting-installation",
      "areaServed": "Watford",
      "serviceType": "Lighting Installation",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/e098daec074ed2b7142484935770ee91.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Certified
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford Lighting Experts
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Lighting Installation<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional lighting installation services for homes and businesses in Watford. From ceiling lights to spotlights, downlights to feature lighting, we create the perfect ambiance for your property.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Lighting Installation Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive lighting solutions for every room in your Watford property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  💡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Ceiling Lights</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Installation of pendant lights, chandeliers, and flush ceiling lights for living rooms, bedrooms, and hallways in Watford homes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  ⭐
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Downlights &amp; Spotlights</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Recessed downlight and spotlight installation for modern, sleek lighting in kitchens, bathrooms, and living spaces throughout Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🎨
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Feature Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Decorative and accent lighting installation to highlight architectural features and create ambiance in your Watford property.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔆
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Track Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Flexible track lighting systems for kitchens, galleries, and commercial spaces in Watford, with adjustable spotlights.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🌙
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Dimmer Switches</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Dimmer switch installation for adjustable lighting control, creating the perfect mood in any room of your Watford home.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏢
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Commercial Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Professional lighting installation for offices, shops, and commercial premises throughout Watford, meeting all safety standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Room by Room */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Lighting for Every Room in Watford
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored lighting solutions for each space in your home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🍳</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Kitchen Lighting</h3>
              <p className="text-gray-600 text-sm">
                Task lighting, under-cabinet lights, and ceiling spotlights for Watford kitchens
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🛁</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Bathroom Lighting</h3>
              <p className="text-gray-600 text-sm">
                IP-rated downlights and mirror lights for safe bathroom illumination
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🛋️</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Living Room Lighting</h3>
              <p className="text-gray-600 text-sm">
                Ambient, task, and accent lighting for comfortable Watford living spaces
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🛏️</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Bedroom Lighting</h3>
              <p className="text-gray-600 text-sm">
                Relaxing ceiling lights, bedside lamps, and dimmable options
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🚪</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Hallway Lighting</h3>
              <p className="text-gray-600 text-sm">
                Welcoming entrance lighting and corridor illumination for Watford homes
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🌳</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Outdoor Lighting</h3>
              <p className="text-gray-600 text-sm">
                Garden, patio, and security lighting for Watford properties
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of Professional Lighting Installation
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why choose expert lighting installation in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ✨
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Perfect Ambiance</h3>
              <p className="text-gray-600 leading-relaxed">
                Create the ideal atmosphere in every room with expertly positioned lighting throughout your Watford home.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Energy Efficiency</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern LED lighting installations reduce energy consumption and lower electricity bills for Watford homeowners.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Safe Installation</h3>
              <p className="text-gray-600 leading-relaxed">
                All lighting installations comply with electrical regulations, ensuring safety in your Watford property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted lighting installation experts in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                Expert lighting installations
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">
                Certified electricians
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🎨</div>
              <h3 className="text-xl font-bold text-white mb-2">Design Advice</h3>
              <p className="text-gray-300 text-sm">
                Expert lighting design
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">Quality Guaranteed</h3>
              <p className="text-gray-300 text-sm">
                Perfect finish every time
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Lighting Installation Across Watford
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional lighting services throughout Watford, Rickmansworth, and surrounding areas
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need Lighting Installation in Watford?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact Beyond Electrical today for expert lighting installation. We'll help you choose and install the perfect lighting for your Watford property.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}